import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Table2, Maximize2, Download, Loader2, BookOpen } from 'lucide-react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import TableContent from './TableContent';
import { supabase } from '../../../../../core/lib/supabase';
import { uploadDocumentToKnowledgeBase } from '../../../../knowledgebase/services/documentUploadService';
import { configService } from '../../../../../shared/services/configService';

interface ResizableTableProps {
  headerRow: string;
  dataRows: string[];
}

const ResizableTable: React.FC<ResizableTableProps> = ({
  headerRow,
  dataRows,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [columnWidths, setColumnWidths] = useState<Record<number, number>>({});
  const [isResizing, setIsResizing] = useState(false);
  const [resizingColumn, setResizingColumn] = useState<number | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isSavingToKB, setIsSavingToKB] = useState(false);

  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  // Parsear headers y datos
  const parseTableRow = (row: string): string[] => {
    return row
      .split('|')
      .map(cell => cell.trim())
      .filter(cell => cell.length > 0);
  };

  const headers = parseTableRow(headerRow);
  const parsedDataRows = dataRows.map(row => parseTableRow(row)).filter(row => row.length > 0);

  const getMinWidth = useCallback((colIndex: number): number => {
    const headerLength = headers[colIndex]?.length || 0;
    const maxDataLength = Math.max(
      ...parsedDataRows.map(row => (row[colIndex] || '').length)
    );
    return Math.max(headerLength * 8, maxDataLength * 8, 80);
  }, [headers, parsedDataRows]);

  const handleMouseDown = useCallback((e: React.MouseEvent, colIndex: number) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setResizingColumn(colIndex);
    startXRef.current = e.clientX;
    
    const th = (e.target as HTMLElement).parentElement;
    startWidthRef.current = th?.offsetWidth || columnWidths[colIndex] || getMinWidth(colIndex);
  }, [columnWidths, getMinWidth]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizing || resizingColumn === null) return;
    
    const diff = e.clientX - startXRef.current;
    const newWidth = Math.max(getMinWidth(resizingColumn), startWidthRef.current + diff);
    
    setColumnWidths(prev => ({
      ...prev,
      [resizingColumn]: newWidth
    }));
  }, [isResizing, resizingColumn, getMinWidth]);

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
    setResizingColumn(null);
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  // Limpiar markdown de texto
  const cleanMarkdown = (text: string): string => {
    return text.replace(/\*\*(.*?)\*\*/g, '$1');
  };

  const getCleanData = () => {
    const cleanHeaders = headers.map(h => cleanMarkdown(h));
    const cleanData = parsedDataRows.map(row => 
      row.map(cell => cleanMarkdown(cell))
    );
    return { cleanHeaders, cleanData };
  };

  // Crear workbook de Excel
  const createExcelWorkbook = () => {
    const { cleanHeaders, cleanData } = getCleanData();
    const wsData = [cleanHeaders, ...cleanData];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    
    const colWidths = cleanHeaders.map((header, idx) => {
      const maxLength = Math.max(
        header.length,
        ...cleanData.map(row => (row[idx] || '').length)
      );
      return { wch: Math.min(Math.max(maxLength + 2, 10), 50) };
    });
    ws['!cols'] = colWidths;
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Data');
    return wb;
  };

  // Descargar Excel
  const handleDownloadExcel = async () => {
    try {
      setIsExporting(true);
      const wb = createExcelWorkbook();
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const fileName = `workops_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      saveAs(blob, fileName);
      toast.success('Excel downloaded', { description: fileName, duration: 3000 });
    } catch (error) {
      console.error('Error downloading Excel:', error);
      toast.error('Error downloading Excel');
    } finally {
      setIsExporting(false);
    }
  };

  // Guardar en Knowledge Base
  const handleSaveToKB = async () => {
    try {
      setIsSavingToKB(true);
      
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        toast.error('User not authenticated');
        return;
      }

      // 1. Buscar KB "Chat project"
      let kbId = '';
      const kbName = 'Chat project';
      
      const { data: kbData, error: kbError } = await supabase
        .from('knowledge_bases')
        .select('id')
        .eq('name', kbName)
        .eq('created_by', user.id);

      if (kbError) throw kbError;

      if (kbData && kbData.length > 0) {
        kbId = kbData[0].id;
      } else {
        // Crear la KB si no existe
        const { data: newKb, error: createKbError } = await supabase
          .from('knowledge_bases')
          .insert({
            name: kbName,
            description: 'Knowledge Base for Chat exported tables',
            created_by: user.id,
            is_global: true,
            type: 'normal'
          })
          .select('id')
          .single();
          
        if (createKbError) throw createKbError;
        kbId = newKb.id;

        // Associate the KB to the active project (workpill)
        const appConfig = configService.getConfig();
        if (appConfig.workpillId) {
          const { error: assocError } = await supabase
            .from('workpill_knowledge_bases')
            .insert({
              workpill_id: appConfig.workpillId,
              knowledge_base_id: kbId
            });
          if (assocError) {
            console.error('Error associating KB to workpill:', assocError);
            // Non-blocking error, we still want to upload the document
          }
        }
      }

      // 2. Preparar el archivo de Excel
      const wb = createExcelWorkbook();
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const documentName = `Table Export ${new Date().toISOString().slice(0, 10)}.xlsx`;
      const fileName = `${documentName.replace(/\s+/g, '_')}`;
      const file = new File([blob], fileName, { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });

      // 3. Subir el documento a la KB con la carpeta "chat"
      const uploadResult = await uploadDocumentToKnowledgeBase({
        file,
        knowledgeBaseId: kbId,
        folderId: null,
        folderName: 'chat',
        folderDescription: 'AI generated docs',
        documentName,
        description: 'Table exported from AI chat'
      });

      if (!uploadResult.success) {
        throw new Error(uploadResult.error || 'Failed to upload document');
      }

      toast.success('Table saved to Knowledge Base', { description: documentName });
    } catch (error) {
      console.error('Error saving to KB:', error);
      toast.error('Error saving to Knowledge Base');
    } finally {
      setIsSavingToKB(false);
    }
  };

  // Abrir en Excel (placeholder)
  const handleOpenInExcel = () => {
    toast.info('Feature coming soon');
  };

  // Validar que hay headers
  if (headers.length === 0) {
    return null;
  }

  // Preview de headers para el badge
  const previewHeaders = headers.slice(0, 3);
  const hasMoreHeaders = headers.length > 3;

  return (
    <>
      {/* Badge compacto */}
      <button
        onClick={() => setIsModalOpen(true)}
        className="flex items-center gap-2.5 my-3 px-4 py-2.5 bg-gradient-to-r from-emerald-50 to-teal-50 hover:from-emerald-100 hover:to-teal-100 border border-emerald-200/80 rounded-xl text-sm transition-all duration-200 cursor-pointer shadow-sm hover:shadow group w-full max-w-full overflow-hidden text-left"
        title="Click to view table"
      >
        {/* Icono de tabla */}
        <div className="p-1.5 bg-white rounded-lg shadow-sm border border-emerald-100 flex-none">
          <Table2 size={16} className="text-emerald-600" />
        </div>
        
        {/* Info de la tabla */}
        <div className="flex flex-col items-start min-w-0 flex-1">
          <span className="font-medium text-emerald-800 truncate w-full">
            Table · {headers.length} cols × {parsedDataRows.length} rows
          </span>
          <span className="text-xs text-emerald-600/70 truncate w-full">
            {previewHeaders.join(', ')}{hasMoreHeaders ? '...' : ''}
          </span>
        </div>
        
        {/* Icono de expandir */}
        <Maximize2 size={14} className="text-emerald-500 opacity-50 group-hover:opacity-100 transition-opacity ml-1 flex-none" />
      </button>

      {/* Modal con la tabla completa */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-5xl w-[90vw] max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-4">
            <DialogTitle>
              <div className="flex items-center gap-3">
                <div className="p-1.5 bg-emerald-50 rounded-lg">
                  <Table2 size={16} className="text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 text-sm">Table Data</h3>
                  <p className="text-xs text-gray-500">{headers.length} columns · {parsedDataRows.length} rows</p>
                </div>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto px-6">
            <TableContent
              headers={headers}
              dataRows={parsedDataRows}
              columnWidths={columnWidths}
              onMouseDown={handleMouseDown}
              isResizing={isResizing}
              resizingColumn={resizingColumn}
            />
          </div>

          <DialogFooter className="px-6 pb-6 pt-4">
            <div className="flex items-center justify-between w-full">
              <p className="text-xs text-gray-400">
                Drag column borders to resize
              </p>
              {/* Botones de acción */}
              <div className="flex items-center gap-2">
                {/* Botón Descargar */}
                <button
                  onClick={handleDownloadExcel}
                  disabled={isExporting || isSavingToKB}
                  className="inline-flex items-center justify-center h-8 w-8 text-gray-500 hover:text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors disabled:opacity-50"
                  title="Download Excel file"
                >
                  {isExporting ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Download size={14} />
                  )}
                </button>

                {/* Botón Guardar en KB */}
                <button
                  onClick={handleSaveToKB}
                  disabled={isSavingToKB || isExporting}
                  className="inline-flex items-center gap-2 h-8 w-8 justify-center text-teal-600 hover:text-teal-700 bg-teal-50 hover:bg-teal-100 border border-teal-200 rounded-lg transition-colors disabled:opacity-50"
                  title="Save to Knowledge Base"
                >
                  {isSavingToKB ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <BookOpen size={14} />
                  )}
                </button>
                
                {/* Botón Abrir en Excel (coming soon) */}
                <button
                  onClick={handleOpenInExcel}
                  className="inline-flex items-center gap-2 h-8 px-3 text-sm font-medium text-emerald-700 hover:text-emerald-800 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-lg transition-colors"
                  title="Open in Microsoft Excel (coming soon)"
                >
                  <span>Open in Excel</span>
                </button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ResizableTable;

